import { and, asc, desc, eq, getTableColumns, ilike, inArray, isNotNull, isNull, ne, or, sql } from "drizzle-orm";

import type { NotificationFeedPreference, WsFeed } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import {
  account,
  feed,
  feedGroup,
  feedGroupMembership,
  feedPreference,
  item,
  permission,
  workspaceCommandAlias,
  workspaceMembership,
  wsMembershipAvailability,
} from "@/db/schema";
import type { Account, WorkspaceMembership } from "@/db/types.ts";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyOf, emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";
import { sqlMembershipIsOffline, sqlMembershipIsOnline } from "@/models/repositories/membershipOfflineSql";

/** PostgreSQL -infinity/infinity round-trip as strings but are invalid in JS Date / PGlite REPL. */
function pgTimestamptzFieldOrNull(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  if (value === "-infinity" || value === "infinity") return null;
  return value;
}

export async function markPermissionsLoaded(feedId: string): Promise<void> {
  await db.update(feed).set({ loadedPermissions: true }).where(eq(feed.id, feedId)).execute();
  emitTableUpdates(["feed"]);
}

export async function resetAllFeedPageFlags(): Promise<void> {
  await db
    .update(feed)
    .set({
      loadedFirstPage: false,
      loadedLastPage: false,
    })
    .execute();
  emitTableUpdates(["feed"]);
}

export async function bulkUpsertFeed(records: WsFeed[]) {
  if (records.length === 0) return;
  const data = records.map((r) => ({
    id: r.id,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    workspaceId: r.workspaceId,
    readOnly: r.readOnly,
    lastOpened: pgTimestamptzFieldOrNull(r.lastOpened),
    latestActivity: pgTimestamptzFieldOrNull(r.latestActivity),
    loadedFirstPage: false,
    loadedLastPage: false,
    loadedEvents: false,
    loadedPermissions: false,
    feedType: r.feedType,
    title: r.title || null,
    isPrivate: r.isPrivate,
    autoJoin: r?.autoJoin,
    latestAliasActivity: pgTimestamptzFieldOrNull(
      (r as unknown as Record<string, unknown>).latestAliasActivity as string | null | undefined,
    ),
    isDm: r.isDm,
    isSilent: r?.isSilent,
    aliasMembershipId: ((r as unknown as Record<string, unknown>).aliasMembershipId as string | null) ?? null,
    isAliasFeed: ((r as unknown as Record<string, unknown>).isAliasFeed as boolean) ?? false,
  }));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(feed, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [feed.id],
      set: {
        updatedAt: sql`"excluded"."updatedAt"`,
        workspaceId: sql`"excluded"."workspaceId"`,
        readOnly: sql`"excluded"."readOnly"`,
        lastOpened: sql`"excluded"."lastOpened"`,
        latestActivity: sql`"excluded"."latestActivity"`,
        loadedFirstPage: sql`"excluded"."loadedFirstPage"`,
        loadedLastPage: sql`"excluded"."loadedLastPage"`,
        loadedEvents: sql`"excluded"."loadedEvents"`,
        loadedPermissions: sql`"feed"."loadedPermissions"`,
        feedType: sql`"excluded"."feedType"`,
        title: sql`"excluded"."title"`,
        isPrivate: sql`"excluded"."isPrivate"`,
        autoJoin: sql`"excluded"."autoJoin"`,
        isDm: sql`"excluded"."isDm"`,
        isSilent: sql`"excluded"."isSilent"`,
        aliasMembershipId: sql`COALESCE("excluded"."aliasMembershipId", "feed"."aliasMembershipId")`,
        isAliasFeed: sql`CASE WHEN "excluded"."isAliasFeed" THEN "excluded"."isAliasFeed" ELSE "feed"."isAliasFeed" END`,
        latestAliasActivity: sql`"excluded"."latestAliasActivity"`,
      },
    },
  });
  emitTableUpdates(["feed"]);
}

export function groupedFeedsQuery({
  workspaceId,
  myCurrentWorkspaceMembershipId,
  allowNonAliasChannels,
  permissionName = "read",
}: {
  workspaceId?: string;
  myCurrentWorkspaceMembershipId?: string;
  allowNonAliasChannels?: boolean;
  permissionName?: "read" | "write";
}) {
  const query = db
    .select({
      id: feed.id,
      title: feed.title,
      workspaceId: feed.workspaceId,
      updatedAt: feed.updatedAt,
      groupId: feedGroupMembership.groupId,
      readOnly: feed.readOnly,
      alias: workspaceCommandAlias.alias,
      permissionName: permission.name,
    })
    .from(feed)
    .innerJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId));
  if (allowNonAliasChannels) {
    query.leftJoin(workspaceCommandAlias, eq(feed.id, workspaceCommandAlias.feedId));
  } else {
    query.innerJoin(workspaceCommandAlias, eq(feed.id, workspaceCommandAlias.feedId));
  }
  query.innerJoin(
    permission,
    and(
      eq(permission.feedId, feed.id),
      eq(permission.name, permissionName),
      eq(permission.enabled, true),
      eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
    ),
  );
  if (!workspaceId || !myCurrentWorkspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Grouped Feeds", query);
  return query;
}

export const feedsIHaveAccessTo = ({
  workspaceId,
  workspaceMembershipId,
  permissionName,
  allowNonAliasChannels,
  allowGroups,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  permissionName: "read" | "write";
  allowNonAliasChannels: boolean;
  allowGroups: boolean;
}) => {
  if (!workspaceId || !workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }

  let whereValues = [eq(feed.workspaceId, workspaceId)];

  if (allowGroups) {
    whereValues.push(isNotNull(feedGroupMembership.groupId));
  } else {
    whereValues.push(isNull(feedGroupMembership.groupId));
  }

  if (!allowNonAliasChannels) {
    whereValues.push(isNotNull(feed.aliasMembershipId));
  }

  const query = db
    .select({
      id: feed.id,
      title: feed.title,
      isAliasFeed: feed.isAliasFeed,
      aliasMembershipId: feed.aliasMembershipId,
      groupId: feedGroupMembership.groupId,
    })
    .from(feed)
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.enabled, true),
        eq(permission.name, permissionName),
      ),
    )
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .orderBy(asc(feed.title))
    .where(and(...whereValues));

  logQuery("Feeds I Have Access To", query);
  return query;
};

export function myActiveFeedsQuery({
  workspaceId,
  myCurrentWorkspaceMembershipId,
  filterCommandAlias,
  permissionName = "read",
}: {
  workspaceId?: string;
  myCurrentWorkspaceMembershipId?: string;
  filterCommandAlias?: boolean;
  permissionName?: "read" | "write";
}) {
  const selectValues = {
    id: feed.id,
    title: feed.title,
    workspaceId: feed.workspaceId,
    updatedAt: feed.updatedAt,
    groupId: feedGroupMembership.groupId,
    readOnly: feed.readOnly,
    permissionName: permission.name,
    // latestActivity: sql`GREATEST( coalesce(MAX(${item.createdAt}), '-infinity'::timestamp), coalesce(${feed.updatedAt}, '-infinity'::timestamp)) as "latestActivity"`,
    latestActivity: sql`COALESCE(${feed.latestActivity}, '1970-01-01T00:00:00.000Z'::timestamp) as "latestActivity"`,
    latestAliasActivity: sql`COALESCE(${feed.latestAliasActivity}, '1970-01-01T00:00:00.000Z'::timestamp) as "latestAliasActivity"`,
    // Always include aliasCount to avoid duplicate queries
    aliasCount: sql`COUNT(${workspaceCommandAlias?.workspaceMembershipId}) FILTER(where ${workspaceCommandAlias.feedId} IS NOT NULL) as "aliasCount"`,
  };

  const query = db
    .select(selectValues)
    .from(feed)
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        eq(permission.enabled, true),
        eq(permission.name, permissionName),
      ),
    )
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .leftJoin(feedGroup, eq(feedGroup.id, feedGroupMembership.groupId))
    // Always use LEFT JOIN to include all feeds, with alias count
    .leftJoin(workspaceCommandAlias, eq(feed.id, workspaceCommandAlias.feedId));

  const whereValues = [eq(feed.workspaceId, workspaceId), eq(feed.isDm, false)];

  // If filtering for command aliases, only include feeds that have them
  if (filterCommandAlias) {
    whereValues.push(isNotNull(workspaceCommandAlias.feedId));
  }

  query
    .groupBy(
      feed.id,
      feed.title,
      feed.workspaceId,
      feed.updatedAt,
      feedGroupMembership.groupId,
      permission.name,
      sql`"latestActivity"`,
      sql`"latestAliasActivity"`,
    )
    .where(and(...whereValues))
    .orderBy(desc(sql`"latestAliasActivity"`), desc(sql`"latestActivity"`));

  if (!workspaceId || !myCurrentWorkspaceMembershipId) {
    return emptyOf(query);
  }
  logQuery("My Active Feeds", query);
  return query;
}

export function topFeedsByActivityQuery({
  workspaceId,
  myCurrentWorkspaceMembershipId,
  limit,
  requireNotificationPreference = false,
}: {
  workspaceId?: string;
  myCurrentWorkspaceMembershipId?: string;
  limit: number;
  requireNotificationPreference?: boolean;
}) {
  const query = db
    .select({
      id: feed.id,
      latestActivity: sql`COALESCE(${feed.latestActivity}, '1970-01-01T00:00:00.000Z'::timestamp) as "latestActivity"`,
      latestAliasActivity: sql`COALESCE(${feed.latestAliasActivity}, '1970-01-01T00:00:00.000Z'::timestamp) as "latestAliasActivity"`,
    })
    .from(feed)
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        eq(permission.enabled, true),
        eq(permission.name, "read"),
      ),
    );

  // Only filter by notification preference if explicitly required
  if (requireNotificationPreference) {
    query.innerJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    );
  }

  query
    .where(and(eq(feed.workspaceId, workspaceId), eq(feed.isDm, false)))
    .groupBy(feed.id, sql`"latestActivity"`, sql`"latestAliasActivity"`)
    .orderBy(desc(sql`"latestAliasActivity"`), desc(sql`"latestActivity"`))
    .limit(limit);

  if (!workspaceId || !myCurrentWorkspaceMembershipId) {
    return emptyOf(query);
  }
  logQuery("Top Feeds By Activity", query);
  return query;
}

export function getChannelsWithPermissions({
  workspaceId,
  myMembership,
}: {
  workspaceId: string;
  myMembership: WorkspaceMembership;
}) {
  const query = db
    .select({
      ...getTableColumns(feed),
      permission_name: sql`${permission.name} as permission_name`,
      wsmid: sql`${permission.workspaceMembershipId} as wsmid`,
      name: account.name,
      email: account.email,
      // title: sql`IFNULL(${feed.title}, 'DM: ' || ${account.name})`,
    })
    .from(feed)
    .innerJoin(permission, eq(permission.feedId, feed.id))
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, permission.workspaceMembershipId))
    .innerJoin(account, eq(account.id, workspaceMembership.accountId))
    .where(
      and(
        eq(feed.workspaceId, workspaceId),
        eq(permission.name, "write"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, myMembership.id),
      ),
    )
    // .groupBy(feed.id)
    .orderBy(asc(feed.isDm), asc(feed.title));

  if (!workspaceId || !myMembership?.id) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Channels with Permissions", query);
  return query;
}

export const fetchAllGroupedChannelsQuery = async ({
  workspaceMembershipId,
  groupId,
}: {
  workspaceMembershipId: string;
  groupId: string;
}) => {
  const query = db
    .selectDistinct({
      id: feed.id,
    })
    .from(feed)
    .innerJoin(item, and(eq(feed.id, item.feedId), eq(item.unread, true)))
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
      ),
    )
    .innerJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .where(eq(feedGroupMembership.groupId, groupId));

  if (!workspaceMembershipId || !groupId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch all grouped channels", query);
  return query;
};
export const fetchAllNonGroupedChannelsQuery = async ({ workspaceMembershipId }: { workspaceMembershipId: string }) => {
  const query = db
    .selectDistinct({
      id: feed.id,
    })
    .from(feed)
    .innerJoin(item, and(eq(feed.id, item.feedId), eq(item.unread, true)))
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .where(isNull(feedGroupMembership.groupId));
  if (!workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch all non grouped channels", query);
  return query;
};
export const nonGroupedChannels = (workspaceId: string) => {
  return db
    .select({
      id: feed.id,
      title: feed.title,
    })
    .from(feed)
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .where(and(isNull(feedGroupMembership.feedId), eq(feed.workspaceId, workspaceId)));
};
export type ChannelOrderType = "driver-activity" | "alpha-asc";
export type ChannelTypeType = "my-channels" | "my-unreads" | "all-channels" | "all-unreads";
export type ChannelList = {
  id: string;
  title: string;
  workspaceId: string;
  isPrivate: boolean;
  enabled: boolean;
  groupId: string;
  groupName: string;
  subscribed: boolean;
  name: string;
  unread: boolean;
  aliasChannel: boolean;
  aliasMembershipId: string | null;
  pushNotificationPreference: NotificationFeedPreference;
  latestActivity?: string;
  hasMultipleAlias?: boolean;
  driverActivity?: string;
  unifiedTimestamp?: string;
  feedLatestActivity?: string;
  feedLatestAliasActivity?: string;
  aliasOwnerAccountId?: string;
  status?: string;
  disabled?: boolean;
  aliasCount?: number;
};
export const fetchChannelList = ({
  channelOrder,
  channelType,
  workspaceId,
  searchValue,
  myAccount,
  myCurrentWorkspaceMembership,
  channelFilters,
  selectedFeedGroups,
}: {
  channelOrder: ChannelOrderType;
  channelType: ChannelTypeType;
  workspaceId: string;
  searchValue: string;
  myCurrentWorkspaceMembership: WorkspaceMembership;
  myAccount: Account;
  channelFilters: string[] | null;
  selectedFeedGroups?: string[];
}) => {
  const workspaceMembershipId = myCurrentWorkspaceMembership?.id;
  const notDM = eq(feed.isDm, false);
  const titleNotNull = isNotNull(feed.title);

  const whereClause = [notDM, titleNotNull];
  if (searchValue?.length > 0) {
    whereClause.push(or(ilike(feed.title, `%${searchValue}%`), ilike(feedGroup.name, `%${searchValue}%`)));
  }

  const driverActivity = sql`
    COALESCE(
    MAX(${item.createdAt}) FILTER(where ${item.isFromAliasMember} = true),
    '-infinity'::timestamp
    )`;
  const latestActivity = sql`GREATEST(${feed.updatedAt} , MAX(${item.createdAt}))`;

  const unifiedTimestamp = sql`
    CASE
        WHEN BOOL_OR(${feed.isAliasFeed}) IS FALSE THEN ${latestActivity}
    ELSE ${driverActivity}
    END
  `;

  const channelsQuery = db
    .select({
      id: feed.id,
      title: feed.title,
      workspaceId: feed.workspaceId,
      isPrivate: feed.isPrivate,
      subscribed: sql`${feedPreference.pushNotificationPreference} != 'none' as "subscribed"`,
      pushNotificationPreference: feedPreference.pushNotificationPreference,
      enabled: permission.enabled,
      name: permission.name,
      groupId: feedGroupMembership.groupId,
      groupName: sql`${feedGroup.name} as "groupName"`,
      aliasChannel: sql`${feed.isAliasFeed} as "aliasChannel"`,
      aliasMembershipId: feed.aliasMembershipId,
      feedLatestActivity: sql`${feed.latestActivity} as "feedLatestActivity"`,
      feedLatestAliasActivity: sql`${feed.latestAliasActivity} as "feedLatestAliasActivity"`,

      unifiedTimestamp: sql`${unifiedTimestamp} as "unifiedTimestamp"`,
      driverActivity: driverActivity.as("driverActivity"),
      latestActivity: latestActivity.as("latestActivity"),
      unread: sql`MAX(${item.createdAt}) FILTER(where ${item.unread} = true AND ${item.deletedAt} IS NULL) as unread`,
      aliasOwnerAccountId: sql`${workspaceMembership.accountId} as "aliasOwnerAccountId"`,
      status: wsMembershipAvailability.status,
      offline: wsMembershipAvailability.offline,
      // Add alias count to detect multiple aliases per feed
      aliasCount: sql`COUNT(DISTINCT ${workspaceCommandAlias.workspaceMembershipId}) FILTER(where ${workspaceCommandAlias.feedId} IS NOT NULL) as "aliasCount"`,
    })
    .from(feed);

  channelsQuery.leftJoin(
    wsMembershipAvailability,
    eq(feed.aliasMembershipId, wsMembershipAvailability.workspaceMembershipId),
  );

  if (selectedFeedGroups?.length > 0) {
    const inArrayValues = inArray(feedGroupMembership.groupId, selectedFeedGroups);
    if (selectedFeedGroups?.includes("non-grouped-channels")) {
      whereClause.push(or(inArrayValues, isNull(feedGroupMembership.groupId)));
    } else {
      whereClause.push(inArrayValues);
    }
  }

  channelsQuery.leftJoin(workspaceMembership, eq(feed.aliasMembershipId, workspaceMembership.id));

  // Join workspaceCommandAlias to count aliases per feed
  channelsQuery.leftJoin(
    workspaceCommandAlias,
    and(eq(workspaceCommandAlias.feedId, feed.id), eq(workspaceCommandAlias.workspaceId, workspaceId)),
  );

  channelsQuery.innerJoin(
    permission,
    and(
      eq(feed.id, permission.feedId),
      eq(permission.workspaceMembershipId, workspaceMembershipId),
      eq(permission.name, "read"),
      eq(permission.enabled, true),
    ),
  );

  switch (channelType) {
    case "my-channels":
      channelsQuery.leftJoin(item, and(eq(feed.id, item.feedId), isNull(item.deletedAt)));
      channelsQuery.innerJoin(
        feedPreference,
        and(
          eq(feed.id, feedPreference.feedId),
          eq(feedPreference.workspaceMembershipId, workspaceMembershipId),
          ne(feedPreference.pushNotificationPreference, "none"),
        ),
      );
      break;
    case "my-unreads":
      channelsQuery.innerJoin(item, and(eq(feed.id, item.feedId), eq(item.unread, true), isNull(item.deletedAt)));
      channelsQuery.innerJoin(
        feedPreference,
        and(
          eq(feed.id, feedPreference.feedId),
          eq(feedPreference.workspaceMembershipId, workspaceMembershipId),
          ne(feedPreference.pushNotificationPreference, "none"),
        ),
      );
      break;
    case "all-channels":
      channelsQuery.leftJoin(item, and(eq(feed.id, item.feedId), isNull(item.deletedAt)));
      channelsQuery.leftJoin(
        feedPreference,
        and(eq(feed.id, feedPreference.feedId), eq(feedPreference.workspaceMembershipId, workspaceMembershipId)),
      );
      break;
  }
  channelsQuery
    .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
    .leftJoin(feedGroup, eq(feedGroup.id, feedGroupMembership.groupId));

  const orIfClause = [];
  if (channelFilters?.length > 0) {
    if (channelFilters.includes("driving")) {
      orIfClause.push(and(eq(wsMembershipAvailability.status, "driving"), sqlMembershipIsOnline()));
    }
    if (channelFilters.includes("not-driving")) {
      orIfClause.push(
        and(
          eq(wsMembershipAvailability.status, "not-driving"),
          sqlMembershipIsOnline(),
          or(
            ne(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false),
            isNull(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled),
          ),
          or(
            ne(wsMembershipAvailability.mostRecentVolumeAudible, false),
            isNull(wsMembershipAvailability.mostRecentVolumeAudible),
          ),
        ),
      );
    }
    if (channelFilters.includes("offline")) {
      orIfClause.push(sqlMembershipIsOffline());
    }
    if (channelFilters.includes("volume-muted")) {
      orIfClause.push(and(eq(wsMembershipAvailability.mostRecentVolumeAudible, false), sqlMembershipIsOnline()));
    }
    if (channelFilters.includes("hands-free-off")) {
      orIfClause.push(
        and(eq(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false), sqlMembershipIsOnline()),
      );
    }
  }

  channelsQuery
    .where(and(...whereClause, or(...orIfClause)))
    .groupBy(
      feed.id,
      feed.title,
      feed.workspaceId,
      feed.isPrivate,
      feed.aliasMembershipId,
      feedGroupMembership.groupId,
      permission.enabled,
      permission.name,
      workspaceMembership.accountId,
      wsMembershipAvailability.status,
      feedPreference.pushNotificationPreference,
      wsMembershipAvailability.offline,
      sql`subscribed`,
      sql`"groupName"`,
      sql`"aliasChannel"`,
      sql`"feedLatestActivity"`,
      sql`"feedLatestAliasActivity"`,
    );

  // nulls last: https://orm.drizzle.team/docs/select#order-by
  if (channelOrder === "driver-activity") {
    channelsQuery.orderBy(desc(sql`"unifiedTimestamp"`));
  } else {
    channelsQuery.orderBy(asc(sql`lower(${feed.title})`));
  }

  if (!workspaceId || !workspaceMembershipId || !myAccount?.id) {
    return emptyOf(channelsQuery);
  }
  logQuery("Fetch channel list", channelsQuery);
  return channelsQuery;
};

export type NonAliasChannelRow = {
  id: string;
  title: string;
  unread: boolean;
  subscribed: boolean;
};

export function nonAliasChannelsQuery({
  workspaceId,
  workspaceMembershipId,
  searchValue,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  searchValue: string;
}) {
  const latestItem = db
    .selectDistinctOn([item.feedId], {
      feedId: item.feedId,
      createdAt: item.createdAt,
    })
    .from(item)
    .where(isNull(item.deletedAt))
    .orderBy(item.feedId, desc(item.createdAt))
    .as("latestItem");

  const hasUnread = db
    .select({
      feedId: item.feedId,
      hasUnread: sql<boolean>`bool_or(${item.unread})`.as("hasUnread"),
    })
    .from(item)
    .where(isNull(item.deletedAt))
    .groupBy(item.feedId)
    .as("hasUnread");

  const whereConditions = [
    eq(feed.workspaceId, workspaceId),
    eq(feed.isAliasFeed, false),
    eq(feed.isDm, false),
    isNotNull(feed.title),
  ];

  if (searchValue.length > 0) {
    whereConditions.push(ilike(feed.title, `%${searchValue}%`));
  }

  const query = db
    .select({
      id: feed.id,
      title: feed.title,
      unread: hasUnread.hasUnread,
      subscribed: sql<boolean>`coalesce(${feedPreference.pushNotificationPreference}, 'none') != 'none'`.as(
        "subscribed",
      ),
    })
    .from(feed)
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(
      feedPreference,
      and(eq(feed.id, feedPreference.feedId), eq(feedPreference.workspaceMembershipId, workspaceMembershipId)),
    )
    .leftJoin(latestItem, eq(latestItem.feedId, feed.id))
    .leftJoin(hasUnread, eq(hasUnread.feedId, feed.id))
    .where(and(...whereConditions))
    .orderBy(
      desc(sql`COALESCE(${hasUnread.hasUnread}, false)`),
      desc(sql`COALESCE(${latestItem.createdAt}, '-infinity'::timestamp)`),
    );

  if (!workspaceId || !workspaceMembershipId) {
    return emptyOf(query);
  }
  logQuery("Non-alias channels", query);
  return query;
}

export function currentFeedQuery({ currentFeedId }: { currentFeedId?: string }) {
  const query = db.select().from(feed).where(eq(feed.id, currentFeedId));

  if (!currentFeedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Current Feed", query);
  return query;
}

export function channelsWithPermissionsSelect({
  workspaceId,
  workspaceMembershipId,
}: {
  workspaceId?: string;
  workspaceMembershipId?: string;
}) {
  const query = db
    .select({
      ...getTableColumns(feed),
      permission_name: sql`${permission.name} as permission_name`,
      wsmid: sql`${permission.workspaceMembershipId} as wsmid`,
      name: account.name,
      email: account.email,
    })
    .from(feed)
    .innerJoin(permission, eq(permission.feedId, feed.id))
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, permission.workspaceMembershipId))
    .innerJoin(account, eq(account.id, workspaceMembership.accountId))
    .where(
      and(
        eq(feed.workspaceId, workspaceId),
        eq(permission.name, "write"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
      ),
    )
    // .groupBy(feed.id)
    .orderBy(asc(feed.isDm), asc(feed.title));

  if (!workspaceId || !workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Channels with Permissions", query);
  return query;
}

export type DuplicateAliasAccount = {
  id: string;
  aliasMembershipId: string;
  isAliasFeed: boolean;
  title: string;
  accountId: string;
  name: string;
};

export const fetchDuplicateAliasFeeds = async ({
  feedIds,
  workspaceId,
}: {
  feedIds: string[];
  workspaceId: string;
}): Promise<DuplicateAliasAccount[]> =>
  db
    .select({
      id: feed.id,
      aliasMembershipId: feed.aliasMembershipId,
      isAliasFeed: feed.isAliasFeed,
      title: feed.title,
      accountId: workspaceMembership.accountId,
      name: account.name,
    })
    .from(feed)
    .innerJoin(workspaceMembership, eq(feed.aliasMembershipId, workspaceMembership.id))
    .innerJoin(account, eq(workspaceMembership.accountId, account.id))
    .where(and(inArray(feed.id, feedIds), eq(feed.workspaceId, workspaceId)));

export const hasFeedsInDatabase = (workspaceId: string) => {
  return db.select({ id: feed.id }).from(feed).where(eq(feed.workspaceId, workspaceId)).limit(1);
};
